/**
 * `<Look>`: a control's chrome written as art markup.
 *
 * ```xml
 * <Look name="plate" for="button panel" radius="6"
 *       textColor="disabled ? alpha(#f0e6d2, 0.4) : #f0e6d2">
 *   <Rect width="w" height="h" rx="6" fill="mix(color, #040508, 0.88)" stroke="color"/>
 * </Look>
 * ```
 *
 * The drawing fills the control's box - `w` and `h` are its size - with the control's state in
 * scope: `hover`, `pressed`, `focused`, `disabled`, `lit`, the look's `color`, `t` on the desktop's
 * clock, and which kind of control it is dressing as `button`, `panel` or `progress`. A progress bar
 * adds `fraction`, `indeterminate` and `phase`. It registers through `registerLook`, so
 * `look="plate"` works on a `Button`, a `Panel` or a `ProgressBar` exactly as a look written in
 * TypeScript does.
 */
import type { Color } from "../../../common/color.js";
import type { Control } from "../../core/Control.js";
import type { Rect } from "../../../common/geometry.js";
import { MarkupError } from "../../markup/errors.js";
import type { XmlElement } from "../../markup/xml.js";
import type { ProgressVisual } from "../../themes/Theme.js";
import { registerLook, type LookContext, type LookKind, type LookShape } from "../looks.js";
import { compileDrawing, paintDrawing, type Drawing } from "./drawing.js";
import { compileExpression, ExpressionError, toColor, toNumber, type Expression, type Value } from "./expr.js";
import { registerLibraryElement } from "./library.js";
import { translation } from "./shapes.js";

const KINDS: readonly LookKind[] = ["button", "panel", "progress"];
const ATTRS = ["name", "for", "textColor", "fontSize", "fontWeight", "center", "animates", "radius", "round"];

interface CompiledLook {
  kinds: readonly LookKind[];
  drawing: Drawing;
  textColor: Expression | null;
  fontSize: Expression | null;
  fontWeight: Expression | null;
  center: boolean;
  animates: Expression | null;
  radius: Expression | null;
  round: boolean;
}

const memories = new WeakMap<Control, Map<object, { value: number; at: number }>>();

function scopeOf(ctx: LookContext, kind: LookKind, progress?: ProgressVisual): (name: string) => Value | undefined {
  const s = ctx.state;
  const flags: Record<string, Value> = {
    hover: s.hovered ? 1 : 0,
    pressed: s.pressed ? 1 : 0,
    focused: s.focused ? 1 : 0,
    disabled: s.disabled ? 1 : 0,
    lit: ctx.lit ? 1 : 0,
    color: ctx.color,
    button: kind === "button" ? 1 : 0,
    panel: kind === "panel" ? 1 : 0,
    progress: kind === "progress" ? 1 : 0,
  };
  if (progress) {
    flags["fraction"] = progress.fraction;
    flags["indeterminate"] = progress.indeterminate ? 1 : 0;
    flags["phase"] = progress.phase;
  }
  return (name) => (Object.hasOwn(flags, name) ? flags[name] : undefined);
}

function paint(look: CompiledLook, ctx: LookContext, kind: LookKind, progress?: ProgressVisual): void {
  const r = ctx.rect;
  let memory: Map<object, { value: number; at: number }> | undefined;
  if (ctx.control) {
    memory = memories.get(ctx.control);
    if (!memory) memories.set(ctx.control, (memory = new Map()));
  }
  paintDrawing(look.drawing, {
    rc: ctx.rc,
    theme: ctx.theme,
    t: ctx.time,
    w: r.width,
    h: r.height,
    matrix: translation(r.x, r.y),
    lookup: scopeOf(ctx, kind, progress),
    ...(memory ? { memory } : {}),
  });
}

/** Evaluate a look attribute with the control's size and state in scope. */
function read(e: Expression, ctx: Pick<LookContext, "rect" | "state" | "lit"> & { color?: Color }, kind: LookKind): Value {
  const flags = scopeOf({ ...ctx, color: ctx.color ?? { r: 0, g: 0, b: 0, a: 1 } } as LookContext, kind);
  return e.evaluate((n) => (n === "w" ? ctx.rect.width : n === "h" ? ctx.rect.height : flags(n)));
}

/** Compile a `<Look>` element and register it under its name. */
export function registerLookMarkup(el: XmlElement): void {
  for (const a of el.attributes) {
    if (!ATTRS.includes(a.name)) throw new MarkupError(`<Look> has no "${a.name}" attribute; it takes ${ATTRS.join(", ")}`, a);
  }
  const attr = (n: string) => el.attributes.find((a) => a.name === n);
  const name = attr("name")?.value;
  if (!name) throw new MarkupError(`<Look> needs a name`, el);
  const compile = (n: string): Expression | null => {
    const a = attr(n);
    if (!a) return null;
    try {
      return compileExpression(a.value);
    } catch (e) {
      if (e instanceof ExpressionError) throw new MarkupError(`<Look name="${name}"> ${n}: ${e.message}`, a);
      throw e;
    }
  };
  const kinds = (attr("for")?.value ?? "button panel")
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((k) => {
      if (!KINDS.includes(k as LookKind)) throw new MarkupError(`<Look> for is ${KINDS.join(", ")}, not "${k}"`, el);
      return k as LookKind;
    });
  const look: CompiledLook = {
    kinds,
    drawing: compileDrawing(el.children),
    textColor: compile("textColor"),
    fontSize: compile("fontSize"),
    fontWeight: compile("fontWeight"),
    center: attr("center")?.value === "true",
    animates: compile("animates"),
    radius: compile("radius"),
    round: attr("round")?.value === "true",
  };

  registerLook(name, {
    ...(kinds.includes("button") ? { button: (ctx: LookContext) => paint(look, ctx, "button") } : {}),
    ...(kinds.includes("panel") ? { panel: (ctx: LookContext) => paint(look, ctx, "panel") } : {}),
    ...(kinds.includes("progress") ? { progress: (ctx: LookContext, p: ProgressVisual) => paint(look, ctx, "progress", p) } : {}),
    ...(look.textColor ? { buttonText: (ctx: LookContext) => toColor(read(look.textColor!, ctx, "button")) } : {}),
    ...(look.fontSize || look.fontWeight
      ? {
          buttonFont: (ctx: LookContext, font) => ({
            ...font,
            ...(look.fontSize ? { size: Math.max(6, toNumber(read(look.fontSize, ctx, "button"))) } : {}),
            ...(look.fontWeight ? { weight: toNumber(read(look.fontWeight, ctx, "button")) } : {}),
          }),
        }
      : {}),
    centerText: look.center,
    animates: (ctx, kind) =>
      look.kinds.includes(kind) &&
      (look.animates
        ? toNumber(read(look.animates, { rect: { x: 0, y: 0, width: 0, height: 0 }, state: ctx.state, lit: ctx.lit }, kind)) !== 0
        : look.drawing.animated),
    shape: (rect: Rect): LookShape => {
      const ctx = { rect, state: { hovered: false, pressed: false, focused: false, disabled: false } as LookContext["state"], lit: false };
      const radius = look.radius ? toNumber(read(look.radius, ctx, "button")) : 0;
      if (!look.round) return { rect, radius };
      return {
        rect: { x: rect.x + rect.width / 2 - radius, y: rect.y + rect.height / 2 - radius, width: radius * 2, height: radius * 2 },
        radius,
      };
    },
  });
}

registerLibraryElement("Look", registerLookMarkup);
